import { useTranslation } from 'react-i18next';
import { useCategoryPickerQuery } from '@/api/queries/categories';

export type CategoryOption = {
    id: string;
    name: string;
};

/** Where a category's translated name lives in the `pages` namespace. */
export const categoryNameKey = (slug: string): string =>
    `seller.categoryNames.${slug}`;

/**
 * Loads the category options for the POS filter from the API
 * (`GET /categories/picker` — every live category, unpaged), reduced to
 * `{ id, name }`.
 *
 * The name is translated by the category's **slug**, not taken from the API's
 * `name`: `name` is whatever language the row was created in (English, for the
 * seeded catalog), and a Georgian seller should read a Georgian filter. A slug
 * with no translation falls back to the API's name, so a category the shop
 * invented still reads as something rather than as a missing key.
 *
 * The query never rejects — a failed envelope surfaces as `isError` so the
 * filter can show an inline message rather than throwing.
 */
export const useCategoryOptions = () => {
    const { t } = useTranslation('pages');
    const { data, isPending } = useCategoryPickerQuery();

    const categories: CategoryOption[] = data?.success
        ? data.response.items.map((item) => ({
              id: item.id,
              name: t(categoryNameKey(item.slug), { defaultValue: item.name }),
          }))
        : [];

    return {
        categories,
        isLoading: isPending,
        isError: data ? !data.success : false,
    };
};
